import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fmtMonto } from 'shared/format';
import API from 'shared/api';
import { FiX, FiSearch } from 'react-icons/fi';
import { SS } from './syncUtils';

export default function SyncReplicaModal({ sede, onClose }) {
  const sedeCod = sede.sede_codigo;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoCodigo, setUltimoCodigo] = useState(null);
  const debRef = useRef(null);

  const cargar = useCallback(
    async (busq, cursor) => {
      setLoading(true);
      try {
        let url = `/sync/replica/${encodeURIComponent(sedeCod)}?limite=50`;
        if (busq) url += `&q=${encodeURIComponent(busq)}`;
        if (cursor) url += `&ultimo_codigo=${encodeURIComponent(cursor)}`;
        const res = await API.get(url);
        if (res.data.status === 'success') {
          setRows((prev) => (cursor ? [...prev, ...res.data.data] : res.data.data));
          setTotal(res.data.total || 0);
          setHasMore(res.data.pagination?.has_more || false);
          setUltimoCodigo(res.data.pagination?.ultimo_codigo || null);
        }
      } catch (e) {
        if (!cursor) setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [sedeCod]
  );

  useEffect(() => {
    cargar('', null);
  }, [cargar]);

  const onBuscar = (val) => {
    setQ(val);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => cargar(val.trim(), null), 350);
  };

  return (
    <div style={SS.overlay} onClick={onClose}>
      <div
        className="sync-modal"
        style={{
          background: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '14px',
            }}
          >
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                Productos sincronizados
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {total.toLocaleString('es')} productos · {sede.sede_nombre || sedeCod}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '4px',
                display: 'flex',
              }}
            >
              <FiX size={20} />
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <FiSearch
              size={16}
              color="#9ca3af"
              style={{
                position: 'absolute',
                left: '13px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            <input
              value={q}
              onChange={(e) => onBuscar(e.target.value)}
              placeholder="Buscar por codigo o nombre..."
              style={{ ...SS.input, paddingLeft: '38px' }}
            />
          </div>
        </div>

        <div
          className="sync-modal-body"
          style={{ padding: '8px 16px 16px', overflowY: 'auto', flex: 1 }}
        >
          {loading && rows.length === 0 && (
            <div
              style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '13px' }}
            >
              Cargando...
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                {q ? 'Sin resultados' : 'Aún no hay productos'}
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {q ? 'Prueba con otra búsqueda.' : 'Aparecerán tras la primera sincronización.'}
              </div>
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.codigo}
              className="sync-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                borderRadius: '10px',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.descripcion || r.codigo}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.codigo}
                  {r.marca ? ` · ${r.marca}` : ''}
                  {r.departamento ? ` · ${r.departamento}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                  ${fmtMonto(r.precio)}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: r.stock > 0 ? '#059669' : '#dc2626',
                    fontWeight: 500,
                  }}
                >
                  {r.stock} en stock
                </div>
              </div>
              {!r.activo && (
                <span style={{ ...SS.chip, background: '#f3f4f6', color: '#9ca3af' }}>
                  Inactivo
                </span>
              )}
            </div>
          ))}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                onClick={() => cargar(q.trim(), ultimoCodigo)}
                disabled={loading}
                style={SS.btnOutline}
              >
                {loading ? 'Cargando...' : 'Ver mas'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
